import { motion } from 'framer-motion';

import { getExtractionDetail } from './procedural-helpers';
import type { ExtractionPhase, ExtractionState } from '@/api/hooks/useProceduralStream';
import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

interface ProceduralExtractionLoaderProps {
  state: ExtractionState;
}

interface StageDescriptor {
  key: Exclude<ExtractionPhase, 'failed'>;
  label: string;
}

const STAGES: StageDescriptor[] = [
  { key: 'reading', label: 'Reading' },
  { key: 'extracting', label: 'Extracting' },
  { key: 'merging', label: 'Reviewing' },
  { key: 'saving', label: 'Saving' },
];

const STAGE_INDEX: Record<Exclude<ExtractionPhase, 'failed'>, number> = {
  reading: 0,
  extracting: 1,
  merging: 2,
  saving: 3,
  done: 4,
};

export function ProceduralExtractionLoader({ state }: ProceduralExtractionLoaderProps) {
  // Failures are surfaced via toast; callers fall through to the empty state.
  if (state.phase === 'failed') return null;
  const detail = getExtractionDetail(state);
  const currentIndex = STAGE_INDEX[state.phase];

  return (
    <div className="flex flex-1 flex-col items-center justify-center py-12">
      <div className="text-center">
        <h2 className="font-serif text-3xl font-medium">Extracting your timetable...</h2>
        <p className="mt-2 text-sm text-gray-600">Once processing finishes, your procedural events will appear here.</p>
        {detail !== null && <p className="mt-3 text-xs text-gray-500">{detail}</p>}
      </div>

      <div className="mt-10 w-full max-w-2xl">
        <div className="relative flex items-start justify-between">
          {STAGES.map((stage, index) => {
            const isActive = index === currentIndex;
            const isComplete = index < currentIndex;

            return (
              <div key={stage.key} className="group relative flex flex-1 flex-col items-center">
                <div className="absolute top-3 right-0 left-0 h-px bg-gray-300 group-first:left-1/2 group-last:right-1/2" />

                <div className="z-10 flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white">
                  {isComplete ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      className="flex items-center justify-center"
                    >
                      <Icon className="lucide--circle-check text-teal-500" />
                    </motion.div>
                  ) : isActive ? (
                    <Icon className="lucide--loader-circle animate-spin text-blue-500 duration-1000" />
                  ) : null}
                </div>

                <div
                  className={cn(
                    'mt-3 rounded-lg border px-2 py-1 text-center text-xs',
                    isActive && 'border-blue-200 bg-blue-100 text-blue-700',
                    isComplete && 'border-green-200 bg-green-100/60 text-green-600',
                    !isActive && !isComplete && 'bg-gray-100 text-gray-600',
                  )}
                >
                  {stage.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
